'use client';
import React from 'react';
import { type ReactNode } from 'react';
import { cx } from '@milanote/roma/utils/cx';
import { Avatar, type AvatarProps } from './avatar';

const SEMANTIC_NAME = 'RomaAvatarLabelGroup';

type AvatarLabelGroupSize = 'xs' | 'sm' | 'md' | 'lg';

const styles = {
    xs: {
        root: 'gap-2',
        title: 'text-xs font-semibold',
        subtitle: 'text-2xs',
        textContainer: 'flex flex-col -space-y-xxs [[data-in-select-item]_&]:-space-y-sm',
    },
    sm: {
        root: 'gap-2',
        title: 'text-sm font-semibold',
        subtitle: 'text-xs',
        textContainer: 'flex flex-col -space-y-xxs [[data-in-select-item]_&]:-space-y-sm',
    },
    md: {
        root: 'gap-2',
        title: 'text-md font-semibold',
        subtitle: 'text-md',
        textContainer: '[[data-in-select-item]_&]:-space-y-xxs',
    },
    lg: {
        root: 'gap-4',
        title: 'text-lg font-semibold',
        subtitle: 'text-display-xs font-normal',
        textContainer: '[[data-in-select-item]_&]:-space-y-xxs',
    },
};

export interface AvatarLabelGroupProps extends Omit<AvatarProps, 'size'> {
    size: AvatarLabelGroupSize;
    title: string | ReactNode;
    subtitle: string | ReactNode;
    /**
     * Optional independent avatar size. If not provided, defaults to the component's size.
     */
    avatarSize?: AvatarProps['size'];
    /**
     * Optional label color. If 'active', uses user-primary color. If undefined, uses default color.
     */
    labelColor?: 'active';
}

export const AvatarLabelGroup = ({
    title,
    subtitle,
    className,
    size,
    avatarSize,
    labelColor,
    ...props
}: AvatarLabelGroupProps) => {
    return (
        <figure className={cx(SEMANTIC_NAME, 'group flex min-w-0 flex-1 items-center', styles[size].root, className)}>
            <Avatar {...props} size={avatarSize || size} />
            <figcaption className={cx('min-w-0 flex-1', styles[size].textContainer)}>
                <div
                    className={cx(
                        'truncate',
                        labelColor === 'active' ? 'text-text-user-primary' : 'text-primary',
                        styles[size].title,
                    )}
                >
                    {title}
                </div>
                <div className={cx('truncate text-tertiary', styles[size].subtitle)}>{subtitle}</div>
            </figcaption>
        </figure>
    );
};

AvatarLabelGroup.displayName = SEMANTIC_NAME;
